import type { CSSProperties } from "react";
import { Input } from "./input";
import { Search, X } from "lucide-react";
import { cn } from "@/lib/utils";

export default function SearchInput({
  className,
  isKbd = true,
  placeholder = "Quick search...",
  bgColor = "white",
  value = "",
  onChange = () => {},
  onEnter,
  expandOnFocus = false,
  collapsedWidth = "13rem",
  collapsedWidthMd = "15rem",
  expandedWidth = "17rem",
  expandedWidthMd = "23rem",
  style,
}: {
  className?: string;
  isKbd?: boolean;
  placeholder?: string;
  bgColor?: string;
  value?: string;
  onChange?: any;
  /** Called on Return / the touch keyboard's "Search" key, before the blur. */
  onEnter?: () => void;
  /**
   * Opt-in: render narrow, then smoothly expand + "pop" on focus (and stay
   * expanded while a query is typed). Implemented purely in CSS via
   * `:focus-within` / `:has()` — see `.searchInput-expandable` in
   * `styles/index.css`. Default `false` keeps every existing usage
   * byte-identical (no extra class, attr, style, or handlers).
   */
  expandOnFocus?: boolean;
  /** Collapsed (blurred, empty) width — mobile / md+. Expanded width on focus. */
  collapsedWidth?: string;
  collapsedWidthMd?: string;
  expandedWidth?: string;
  expandedWidthMd?: string;
  style?: CSSProperties;
}) {
  return (
    <div
      className={cn(
        "relative max-w-[28rem] shrink-0",
        expandOnFocus && "searchInput-expandable",
        className
      )}
      style={
        expandOnFocus
          ? ({
              "--si-w-collapsed": collapsedWidth,
              "--si-w-collapsed-md": collapsedWidthMd,
              "--si-w-expanded": expandedWidth,
              "--si-w-expanded-md": expandedWidthMd,
              ...style,
            } as CSSProperties)
          : style
      }
    >
      <div
        className={cn(
          `relative flex items-center border border-[var(--color-black-2)] rounded-[0.75rem] overflow-hidden h-[2rem] md:h-[2.5rem] bg-[${bgColor}]`,
          expandOnFocus && "searchInput-pill"
        )}
      >
        <div
          className={cn(
            "absolute left-2 md:left-4 flex items-center justify-center pointer-events-none"
          )}
        >
          <Search
            className={cn(
              "size-[1rem] md:size-[1.5rem] text-[var(--color-black-6)]"
            )}
          />
        </div>
        <Input
          type="text"
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          // Search runs as you type — Return / "Search" on a touch keyboard
          // just dismisses it (iPad never does on its own).
          enterKeyHint="search"
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              onEnter?.();
              e.currentTarget.blur();
            }
          }}
          className={cn(
            "pl-4 md:pl-12 pr-4 md:pr-20 h-10 border-0  text-b2 text-[var(--color-black-6)] tracking-[-0.32px] placeholder:text-[var(--color-black-6)] placeholder:text-b2 focus-visible:ring-0 focus-visible:ring-offset-0  searchInput-input"
          )}
        />
        <div className="absolute right-2 flex items-center gap-1.5">
          {value && (
            <button
              type="button"
              data-keep-keyboard
              onClick={() => onChange({ target: { value: "" } } as any)}
              className="flex items-center justify-center size-5 rounded-md hover:bg-[var(--color-black-1)] transition-colors"
            >
              <X className="size-3.5 text-[var(--color-black-6)]" />
            </button>
          )}
          {isKbd && (
            <kbd
              className={cn(
                "bg-[var(--color-black-1)] border border-[var(--color-black-1)] rounded-lg px-1"
              )}
            >
              <span
                className={cn(
                  "inline-flex items-center gap-[2px] text-[14px] leading-[1] text-[var(--color-black-9)] tracking-[-0.28px] opacity-50"
                )}
              >
                <span className={cn("text-[var(--color-black-9)]")}>⌘</span>
                <span className={cn("text-[var(--color-black-9)]")}>K</span>
              </span>
            </kbd>
          )}
        </div>
      </div>
    </div>
  );
}
